import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Combobox } from "@cayuse/shared-react.components.form.combobox";
import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import PropTypes from "prop-types";
import { Button } from "semantic-ui-react";
import { api } from "services";


import { lookupSingleEntryLabel } from "containers/Rules/List/ConditionBuilder/utils";
import { CAYUSE_TYPES } from "globals/constants";
import { useDictionary } from "hooks";
import { tenantSelectors } from "store/selectors";
import { isFieldPicklist, findPicklistOptionsByFieldId, getResearchTeamRoles, isFieldCheckbox } from "utils";

import {
	toBasicCodeOptions,
	toHERDCCodeOptions,
	toTOACodeOptions,
} from "../flowable/ClassificationCodes/helpers";
import CodeSearch from "../flowable/ClassificationCodes/internal/CodeSearch";
import styles from "./styles.css";
import { isSearchableField } from "./utils";

const CHECKBOX_OPTIONS = [
	{ id: "unchecked", label: "False" },
	{ id: "checked",label: "True" }
];

const getSearchOptionsConfig = selectedField => {
	return {
		[CAYUSE_TYPES.FUNDING_PROGRAM]: {
			query: api.fundingPrograms.findAll,
			transformOption: option => {
				return {
					value: option.fundingProgramId,
					label: `${option.organizationName} - ${option.fundingProgramName}`,
				};
			},
		},
		[CAYUSE_TYPES.ORG_FINDER]: {
			query: api.organization.findAll,
			transformOption: option => {
				return {
					value: option.id,
					label: option.name,
				};
			},
		},
		[CAYUSE_TYPES.PERSON_FINDER]: {
			query: params => api.users.searchByFullName(params.search),
			transformOption: option => {
				return {
					value: option.personId,
					label: option.fullName,
				};
			},
		},
		[CAYUSE_TYPES.UNIT_FINDER]: {
			query: api.unit.findAll,
			transformOption: option => {
				return {
					value: option.id,
					label: option.name,
				};
			},
		},
	}[selectedField.cayuseType];
};

const AuNzCodeSearch = props => {
	const [ value, setValue ] = useState("");

	const toCodeOptions = useCallback((options, searchOptions = true) => {
		let codeOptions = [];

		if (searchOptions) {
			const codeType = props.field.extraSettings.codeType.toLowerCase();
			if (codeType === "toa") {
				codeOptions = toTOACodeOptions(options);
			} else if (codeType === "herdc") {
				codeOptions = toHERDCCodeOptions(options, props.field.extraSettings.excludeSubcategories);
			} else {
				codeOptions = toBasicCodeOptions(options, props.field.extraSettings.limitToFourDigits);
			}
		} else {
			codeOptions = options;
		}

		return codeOptions.map(option => {
			return {
				...option,
				selected: false,
				"data-value": option.value,
				"data-text": option.text,
				disabled: option.level !== option.maxlevel,
			};
		});
	}, [ props.field.extraSettings ]);

	return (
		<CodeSearch
			code={value}
			placeholder={props.placeholder}
			codeType={props.field.extraSettings.codeType.toLowerCase()}
			toCodeOptions={toCodeOptions}
			onChangeCode={codeValue => {
				setValue(codeValue);
				props.onChange(codeValue);
			}}
		/>
	);
};

const SearchOptions = ({ onChange, field, checkedValue }) => {
	const fundingSchemeDictionary = useSelector(tenantSelectors.useFundingSchemeDictionary);
	const dictionary = useDictionary("ruleBuilder");
	const [ comboboxValue, setComboboxValue ] = useState(null);

	useEffect(() => {
		const optionId = checkedValue?.split("#")[1];

		if (!optionId || optionId === "*") {
			return;
		}

		lookupSingleEntryLabel([ field ], field.id, optionId).then(label => {
			if (label) {
				setComboboxValue({ value: optionId, label });
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadOptions = async inputValue => {
		const { query, transformOption } = getSearchOptionsConfig(field);

		const response = await query({
			search: inputValue,
		});

		return {
			options: response.content.map(transformOption),
			hiddenOptionsCount: response.totalPages > 1 && response.totalElements - response.content.length,
		};
	};

	const getPlaceholder = () => {
		//eslint-disable-next-line default-case
		switch (field.cayuseType) {
		case CAYUSE_TYPES.FUNDING_PROGRAM:
			return fundingSchemeDictionary ? dictionary.selectFundingScheme : dictionary.selectFundingProgram;
		case CAYUSE_TYPES.ORG_FINDER:
			return dictionary.selectExternalOrganization;
		case CAYUSE_TYPES.PERSON_FINDER:
			return dictionary.selectPerson;
		case CAYUSE_TYPES.UNIT_FINDER:
			return dictionary.selectUnit;
		case CAYUSE_TYPES.CLASSIFICATION_CODES:
			return dictionary.selectClassificationCode;
		}
	};

	if (field.cayuseType === CAYUSE_TYPES.CLASSIFICATION_CODES) {
		return (
			<div className={styles.searchableOptionWrapper} data-testid="search-options">
				<AuNzCodeSearch
					field={field}
					onChange={value => {
						onChange(`${field.id}#${!value ? "*" : value}`);
					}}
					placeholder={getPlaceholder()}
				/>
			</div>
		);
	}

	return (
		<div className={styles.searchableOptionWrapper} data-testid="search-options">
			<Combobox
				loadOptions={loadOptions}
				placeholder={getPlaceholder()}
				value={comboboxValue}
				onChange={option => {
					setComboboxValue(option ?? null);
					onChange(`${field.id}#${!option ? "*" : option.value}`, option?.label);
				}}
			/>
		</div>
	);
};
export default SearchOptions;

SearchOptions.displayName = "SearchOptions";

SearchOptions.propTypes = {
	onChange: PropTypes.func.isRequired,
	field: PropTypes.object.isRequired,
	checkedValue: PropTypes.string,
};

export const RenderOptions = ({ activeQuestion, onAddRule, onAddAnswer }) => {
	if (isSearchableField(activeQuestion)) {
		return (
			<SearchOptions
				onChange={(value, label) => {
					if (value) {
						const idValue = value.toString().split("#");
						if(idValue[1] === "*") {
							return;
						}
						activeQuestion.itemText = label;
						activeQuestion.itemId = idValue[1];
						onAddRule(activeQuestion);
					}}}
				field={activeQuestion}
			/>
		);
	} else {
		return (
			<ul className={styles.optionList}>
				{activeQuestion.items.map((option, index) => (
					<li key={index} className={styles.panelEntry}>
						<div className={styles.addButton}>
							<Button onClick={() => onAddAnswer(option, index)} tertiary icon="plus" size="mini"/>
						</div>
						{option.text}
					</li>
				))}
			</ul>
		);
	}
};

RenderOptions.displayName = "RenderOptions";

RenderOptions.propTypes = {
	activeQuestion: PropTypes.object.isRequired,
	onAddRule: PropTypes.func.isRequired,
	onAddAnswer: PropTypes.func.isRequired
};

export const OptionList = props => {
	const {
		selectedField,
		checkedValue,
		onChange,
	} = props;

	const dictionary = useDictionary("ruleBuilder");

	const [ isLoadingOptions, setIsLoadingOptions ] = useState(false);
	const [ availableOptions, setAvailableOptions ] = useState([]);

	const isSearchableOptions = isSearchableField(selectedField);

	/**
	 * Sets available options as boolean-false when field isn't a picklist, empty array when no options exist
	 */
	useEffect(() => {
		if ((!isFieldPicklist(selectedField) && !isFieldCheckbox(selectedField)) || isSearchableOptions) {
			if (isNonEmptyArray(availableOptions)) {
				setAvailableOptions([]);
			}

			return;
		}
		setIsLoadingOptions(true);

		if (selectedField.cayuseType === CAYUSE_TYPES.RESEARCH_TEAM) {
			let roleId;
			if (checkedValue?.length > 0) {
				const valueId = checkedValue.toString().split("#")[1];
				if (valueId !== "*") {
					roleId = valueId;
				}
			}
			getResearchTeamRoles({ ids: [ roleId ] })
				.then(roles => setAvailableOptions(roles))
				.finally(() => setIsLoadingOptions(false));
		} else if (isFieldCheckbox(selectedField)) {
			setAvailableOptions(CHECKBOX_OPTIONS);
			setIsLoadingOptions(false);
		}else {
			findPicklistOptionsByFieldId(selectedField)
				.then(({ options }) => {
					setAvailableOptions(options);
				})
				.finally(() => setIsLoadingOptions(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ selectedField ]);

	const handleChange = e => {
		onChange(e.target.value);
	};

	if (isSearchableOptions) {
		return (
			<SearchOptions
				key={selectedField.id}
				field={selectedField}
				checkedValue={checkedValue}
				onChange={onChange}
			/>
		);
	}
	else if (isLoadingOptions) {
		return (
			<div className={styles.loadingIndicatorWrapper} data-testid="options-loading">
				<LoadingIndicator />
			</div>
		);
	}
	else if (!isFieldPicklist(selectedField) && !isFieldCheckbox(selectedField)) {
		return (
			<div className={styles.noOptions} data-testid="no-options-apply">
				{dictionary.noOptionsApply || "No options apply to this question."}
			</div>
		);
	}
	else if (availableOptions.length === 0) {
		return (
			<div className={styles.noOptions} data-testid="empty-options">
				{dictionary.noOptionsAvailable || "No options have been supplied for this question."}
			</div>
		);
	}

	return (
		<ul className={styles.optionList}>
			{availableOptions.map(({ id: optionId, label }, index) => {
				const optionLabel = isNonEmptyString(label) ? label : "Unknown Option";
				const checkboxValue = `${selectedField.id}#${optionId}`;
				const isChecked = checkedValue === checkboxValue;
				const isDiscreteValue = isNonEmptyString(checkedValue) && !/#\*$/.test(checkedValue);
				const isDisabled = isDiscreteValue && !isChecked;
				const classes = crunchClasses({
					[styles.panelEntry]: true,
					[styles.disabled]: isDisabled
				});

				return (
					<li key={`${selectedField.id}-option-${index}`} className={classes}>
						<Checkbox
							checked={isChecked}
							disabled={isDisabled}
							value={checkboxValue}
							onChange={handleChange}
							data-testid={`option-${index}-checkbox`}
						/>
						<label className={styles.label} data-tip={optionLabel}>{optionLabel}</label>
					</li>
				);
			})}
		</ul>
	);
};

OptionList.displayName = "OptionList";

OptionList.propTypes = {
	selectedField: PropTypes.object.isRequired,
	checkedValue: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};
